import {
  Children,
  cloneElement,
  isValidElement,
  ReactElement,
  useCallback,
  useState,
} from 'react';

import { TStatus, EStatus } from '@skillarea/lib-shared';
import { useStore } from '@tanstack/react-form';

import { useFormFieldTexts } from './FormFieldTextsContext';

interface TFormFieldProps<Value> {
  forceStatus?: TStatus;
  forceTouched?: boolean;
  forceValue?: Value;
  isLikeErrorOnWarning?: boolean;
  isShowErrorWhenEditing?: boolean;
  onEditFinished?: () => void;
  successMessage?: string;
  form: any;
  name: string;
  children: ReactElement;
}

export const FormField = ({
  children,
  forceStatus,
  forceTouched,
  forceValue,
  isLikeErrorOnWarning,
  isShowErrorWhenEditing,
  name,
  onEditFinished,
  successMessage,
  form,
}: TFormFieldProps<unknown>) => {
  const { fieldRequired } = useFormFieldTexts();
  const [focused, setFocused] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  // 1. SUBSCRIBE TO GLOBAL ERRORS
  // Subscribe to fieldErrors and fieldMeta changes for this field.
  // When the form updates errors, FormField will re-render.
  const externalErrors = useStore(form.store, (state: any) => {
    const map = state.errorMap;
    if (!map) return [];

    // Object.values(map) returns an array of error objects:
    // [{ password: {...} }, { password: {...} }] (for onChange, onBlur, etc.)
    return Object.values(map).reduce((acc: string[], triggerGroup: any) => {
      // Find this field's data (name) inside the current group
      const fieldData = triggerGroup?.[name];

      if (fieldData?.errors && Array.isArray(fieldData.errors)) {
        return [...acc, ...fieldData.errors];
      }
      return acc;
    }, []);
  });

  const handleEditFinished = useCallback(() => {
    setIsEditing(false);
    onEditFinished?.();
  }, [onEditFinished]);

  const handleFocus = useCallback(() => {
    setFocused(true);
  }, []);

  const handleIsEditing = useCallback(() => {
    setIsEditing(true);
  }, []);

  return (
    <form.Field name={name}>
      {(field: any) => {
        const value = field.state.value;

        // Take the field's own errors (client-side validation)
        let errors = [...field.state.meta.errors];

        errors = Array.from(new Set([...errors, ...externalErrors]));

        const touched = forceTouched ?? field.state.meta.isTouched;

        let status: TStatus | undefined = forceStatus;
        let message = status ? [status] : '';

        if (!status) {
          if (errors.length > 0) {
            status = 'error';
            message = errors.join(', ');
          } else if (touched && !value) {
            status = 'warning';
            message = fieldRequired;
          } else {
            status = 'success';
            message = successMessage ?? '';
          }
        }

        if (isLikeErrorOnWarning && status === EStatus.warning)
          status = EStatus.error;

        const onChange = (v: unknown) => {
          handleIsEditing();
          field.handleChange(v);
        };

        const onBlur = () => {
          field.handleBlur();
          handleEditFinished();
        };

        const child = Children.only(children);
        if (!isValidElement(child)) throw new Error('must be element');

        const childProps: Record<string, unknown> = {
          disabled: (child.props as Record<string, unknown>).disabled,
          status: touched ? status : undefined,
          ...(typeof child.type !== 'string' && focused
            ? { tooltipStatus: status }
            : {}),
          value:
            forceValue ??
            (child.props as Record<string, unknown>).value ??
            value,
          name,
          onChange,
          onBlur,
          onFocus: handleFocus,
        };

        if (touched && status && (isShowErrorWhenEditing || !isEditing)) {
          (childProps as Record<string, unknown>).messages = message;
        }

        return cloneElement(child as ReactElement, childProps);
      }}
    </form.Field>
  );
};
